"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { getTemplateBySlug } from "@/lib/templates";

export interface TemplateEntry {
  name: string;
  slug: string;
  category: string;
  price: string;
  demoUrl: string;
  src: string;
  /** Still of the invitation's opening frame — see scripts/generate-template-posters.mjs */
  poster: string;
  controllable?: boolean;
}

export const TEMPLATES: TemplateEntry[] = [
  {
    name: "Temple Theme",
    slug: "temple-theme",
    category: "Premium",
    price: "3,999",
    src: "/invitation/vijay-rashmika-wedding-invitation?to=Guest&embed=true&embedDelay=0",
    poster: "/assets/homepage/posters/temple-theme.webp",
    demoUrl: "/invitation/vijay-rashmika-wedding-invitation?to=Guest&demo=true",
    controllable: true,
  },
  {
    name: "Signature of Love",
    slug: "signature-of-love",
    category: "Premium",
    price: "2,999",
    src: "/invitation/signature-of-love?to=Guest&embed=true&embedDelay=0",
    poster: "/assets/homepage/posters/signature-of-love.webp",
    demoUrl: "/invitation/signature-of-love?to=Guest&demo=true",
    controllable: true,
  },
  {
    name: "Royal Premium",
    slug: "royal-premium",
    category: "Premium",
    price: "3,999",
    src: "/invitation/priya-arjun-wedding?to=Guest&embed=true&embedDelay=0",
    poster: "/assets/homepage/posters/royal-premium.webp",
    demoUrl: "/invitation/priya-arjun-wedding?to=Guest&demo=true",
    controllable: true,
  },
  {
    name: "South Indian",
    slug: "south-indian",
    category: "Premium",
    price: "3,999",
    src: "/invitation/traditional-invitation?to=Guest&embed=true&embedDelay=0",
    poster: "/assets/homepage/posters/south-indian.webp",
    demoUrl: "/invitation/traditional-invitation?to=Guest&demo=true",
    controllable: true,
  },
  {
    name: "Christian Elegance",
    slug: "christian-elegance",
    category: "Premium",
    price: "3,999",
    src: "/invitation/christian-elegance?to=Guest&embed=true&embedDelay=0",
    poster: "/assets/homepage/posters/christian-elegance.webp",
    demoUrl: "/invitation/christian-elegance?to=Guest&demo=true",
    controllable: true,
  },
  {
    name: "Classic",
    slug: "classic",
    category: "Standard",
    price: "1,999",
    src: "/api/embed-proxy?url=https://invite.myshaadhilink.in/invitation/arjun-priya-wedding&delay=3000",
    poster: "/assets/homepage/posters/classic.webp",
    demoUrl: "https://invite.myshaadhilink.in/invitation/arjun-priya-wedding",
  },
];

/**
 * A phone frame showing a still of the invitation, which swaps to the real
 * thing once the visitor actually engages with the card.
 *
 * Five of these render on the homepage and again on /templates. Mounting every
 * iframe up front meant five full copies of the invitation app booted on load:
 * 422 requests for 168 unique files, every font pulled 6-8 times, ~14 MB of
 * template artwork, 17s of main-thread work, and five spurious GA4 pageviews
 * plus five Google Ads conversion pings per visit. The poster is ~30-75 KB.
 */
function PhonePreview({ template }: { template: TemplateEntry }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(0.6);
  const [showTap, setShowTap] = useState(false);
  // Mounts the iframe. One-way: once loaded it stays, so a second hover is instant.
  const [activated, setActivated] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The pointer can leave before the iframe finishes loading; without this the
  // invitation would start playing under a cursor that has already moved on.
  const hoveringRef = useRef(false);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const update = () => setScale(frame.clientWidth / 375);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(frame);
    return () => ro.disconnect();
  }, []);

  const startPlayback = useCallback(() => {
    if (!template.controllable) return;
    iframeRef.current?.contentWindow?.postMessage("embedStart", "*");
  }, [template.controllable]);

  const handleEnter = useCallback(() => {
    hoveringRef.current = true;
    setActivated(true);
    if (!template.controllable) return;
    setShowTap(true);
    tapTimerRef.current = setTimeout(() => {
      setShowTap(false);
      startPlayback();
    }, 700);
  }, [template.controllable, startPlayback]);

  const handleLeave = useCallback(() => {
    hoveringRef.current = false;
    if (!template.controllable) return;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    setShowTap(false);
    iframeRef.current?.contentWindow?.postMessage("embedStop", "*");
  }, [template.controllable]);

  useEffect(() => {
    return () => {
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    };
  }, []);

  return (
    <div
      className="iv-tpl__phone"
      ref={frameRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <Image
        src={template.poster}
        alt=""
        aria-hidden="true"
        fill
        sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 320px"
        className={`iv-tpl__poster${frameReady ? " is-hidden" : ""}`}
      />

      {activated && (
        <div className="iv-tpl__scaler" style={{ transform: `scale(${scale})` }}>
          <iframe
            ref={iframeRef}
            src={template.src}
            className="iv-tpl__iframe"
            title={`${template.name} Preview`}
            onLoad={() => {
              setFrameReady(true);
              if (hoveringRef.current && !showTap) startPlayback();
            }}
          />
        </div>
      )}

      {showTap && (
        <div className="iv-tpl__tap-overlay">
          <div className="iv-tpl__tap-ripple" />
        </div>
      )}
    </div>
  );
}

export function TemplateCard({ template }: { template: TemplateEntry }) {
  const productUrl = `/templates/${template.slug}`;
  const isExternal = template.demoUrl.startsWith("http");
  const anchorLabel = getTemplateBySlug(template.slug)?.anchorLabel ?? template.name;

  return (
    <div
      className="iv-tpl__card iv-tpl__card--clickable"
      onClick={() => { window.location.href = productUrl; }}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") window.location.href = productUrl; }}
    >
      <div className="iv-tpl__phone-wrapper">
        <PhonePreview template={template} />
      </div>

      <div className="iv-tpl__info">
        {/* Real anchor so crawlers can reach the product page — the card's JS
            onClick is invisible to them (this was why new templates sat at
            "Discovered - currently not indexed"). */}
        <h3 className="iv-tpl__name">
          <a
            href={productUrl}
            className="iv-tpl__name-link"
            title={anchorLabel}
            onClick={(e) => e.stopPropagation()}
          >
            {template.name}
          </a>
        </h3>
        <p className="iv-tpl__category">{template.category}</p>
        <p className="iv-tpl__price iv-font-display">
          <span className="iv-tpl__currency">₹</span>{template.price}
        </p>

        <div className="iv-tpl__actions">
          <a
            href={template.demoUrl}
            className="iv-tpl__btn iv-tpl__btn--demo"
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            Demo
          </a>
          <a
            href={`https://wa.me/919553143929?text=Hi, I'm interested in the ${template.name} template (₹${template.price})`}
            className="iv-tpl__btn iv-tpl__btn--buy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Buy Now
          </a>
        </div>
      </div>
    </div>
  );
}

// Grid-only component for use in Similar Templates section
export function TemplateGrid({ excludeSlug }: { excludeSlug?: string }) {
  const filteredTemplates = excludeSlug
    ? TEMPLATES.filter(t => t.slug !== excludeSlug)
    : TEMPLATES;

  return (
    <div className="iv-tpl__grid">
      {filteredTemplates.map((template) => (
        <TemplateCard key={template.name} template={template} />
      ))}
    </div>
  );
}

export default function TemplateShowcase() {
  return (
    <section className="iv-tpl" id="templates">
      <div className="iv-tpl__inner">
        <div className="iv-tpl__header">
          <span className="iv-tpl__eyebrow">Our Collection</span>
          <h2 className="iv-tpl__title iv-font-display">Invitation Demos</h2>
          <p className="iv-tpl__subtitle">
            Handcrafted with cultural authenticity and smooth animations.
          </p>
        </div>

        <div className="iv-tpl__grid">
          {TEMPLATES.map((template) => (
            <TemplateCard key={template.name} template={template} />
          ))}
        </div>

        {/* Hidden until we have more templates
        <div className="iv-tpl__cta">
          <a href="/templates" className="iv-btn iv-btn--primary">
            View All Templates
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </a>
        </div>
        */}
      </div>
    </section>
  );
}
